import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { Upload, X, Plus, Save, Lock } from "lucide-react";

export default function SettingsPage() {
  const [s, setS] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => { (async () => { const { data } = await api.get("/settings"); setS(data); })(); }, []);
  if (!s) return <p className="text-sm text-[color:var(--muted-text)]">Loading…</p>;

  const update = (k, v) => setS({ ...s, [k]: v });

  const upload = async (file, folder, cb) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file); fd.append("folder", folder);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/files/${data.path}`;
      cb(url); toast.success("Uploaded");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setUploading(false); }
  };

  const save = async () => {
    setSaving(true);
    try { await api.put("/admin/settings", s); toast.success("Settings saved"); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const updateField = (idx, patch) => {
    const next = [...(s.check_in_fields || [])];
    next[idx] = { ...next[idx], ...patch };
    update("check_in_fields", next);
  };

  return (
    <div className="space-y-6" data-testid="settings-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl text-[color:var(--deep)]">Hotel settings</h1>
          <p className="text-sm text-[color:var(--muted-text)] mt-1">Public content, contacts and check-in rules.</p>
        </div>
        <Button onClick={save} disabled={saving} data-testid="save-settings-btn" className="bg-[color:var(--brand)] hover:bg-[color:var(--brand-dark)]">
          <Save className="w-4 h-4 mr-1" /> {saving ? "Saving…" : "Save changes"}
        </Button>
      </div>

      <Card>
        <CardHeader><CardTitle>Role &amp; access matrix</CardTitle>
          <p className="text-sm text-[color:var(--muted-text)]">What each role can see and do. Use the <b>Team</b> page to create staff accounts and pick a role.</p>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto rounded-lg border border-[color:var(--brand)]/10">
            <table className="w-full text-sm" data-testid="role-matrix">
              <thead className="bg-[color:var(--sand)] text-[color:var(--deep)]">
                <tr>
                  <th className="text-left px-3 py-2 font-semibold">Feature</th>
                  <th className="px-2 py-2 text-center text-xs">Owner<br/><span className="font-normal text-[color:var(--brand)]">super_admin</span></th>
                  <th className="px-2 py-2 text-center text-xs">Admin</th>
                  <th className="px-2 py-2 text-center text-xs">Hotel Manager</th>
                  <th className="px-2 py-2 text-center text-xs">Receptionist</th>
                </tr>
              </thead>
              <tbody>
                {[
                  ["Dashboard & analytics",       true, true, true, false],
                  ["Bookings — view & search",    true, true, true, true ],
                  ["Confirm payments",            true, true, true, true ],
                  ["Digital check-in / check-out",true, true, true, true ],
                  ["Room service charges",        true, true, true, true ],
                  ["Housekeeping board",          true, true, true, true ],
                  ["Room types — create & edit",  true, true, false, false],
                  ["Room units — create & edit",  true, true, "view", false],
                  ["Expenses — log & manage",     true, true, true, false],
                  ["Reports (CSV downloads)",     true, true, true, false],
                  ["Hotel settings",              true, true, false, false],
                  ["Team — view members",         true, true, true, false],
                  ["Team — add members",          true, true, false, false],
                  ["Team — delete members",       true, false, false, false],
                  ["Cancel a booking",            true, true, false, false],
                  ["Send / download invoice",     true, true, true, true ],
                ].map(([label, ...cells], i) => (
                  <tr key={i} className={i % 2 ? "bg-white" : "bg-[color:var(--sand)]/40"}>
                    <td className="px-3 py-2 text-[color:var(--deep)]">{label}</td>
                    {cells.map((v, j) => (
                      <td key={j} className="px-2 py-2 text-center">
                        {v === true ? <span className="inline-flex w-5 h-5 rounded-full bg-emerald-100 text-emerald-700 items-center justify-center text-xs">✓</span> :
                         v === false ? <span className="inline-flex w-5 h-5 rounded-full bg-slate-100 text-slate-400 items-center justify-center text-xs">—</span> :
                         <span className="text-[10px] font-semibold text-amber-700 bg-amber-100 rounded px-1.5 py-0.5 uppercase">{v}</span>}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-[color:var(--muted-text)] mt-3">Role permissions are enforced by the backend on every request — a signed-in user cannot access something outside their role even by opening the URL directly.</p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>Identity</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div><Label>Hotel name</Label><Input value={s.hotel_name} onChange={(e) => update("hotel_name", e.target.value)} data-testid="settings-name" /></div>
          <div><Label>Tagline</Label><Input value={s.tagline} onChange={(e) => update("tagline", e.target.value)} data-testid="settings-tagline" /></div>
          <div className="md:col-span-2"><Label>Description</Label><Textarea rows={3} value={s.description} onChange={(e) => update("description", e.target.value)} data-testid="settings-description" /></div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>Contact &amp; social</CardTitle>
          <p className="text-sm text-[color:var(--muted-text)]">Shown on the landing page footer, contact section and the floating WhatsApp chat button.</p>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2"><Label>Address (drives the map)</Label><Input value={s.address} onChange={(e) => update("address", e.target.value)} data-testid="settings-address" placeholder="e.g. Sokoine Road, Arusha, Tanzania" /></div>
          <div><Label>Phone</Label><Input value={s.phone} onChange={(e) => update("phone", e.target.value)} data-testid="settings-phone" /></div>
          <div><Label>Email</Label><Input value={s.email} onChange={(e) => update("email", e.target.value)} data-testid="settings-email" /></div>
          <div><Label>WhatsApp (opens floating chat)</Label><Input value={s.whatsapp || ""} onChange={(e) => update("whatsapp", e.target.value)} data-testid="settings-whatsapp" placeholder="+255 754 123 456" /></div>
          <div><Label>Currency code</Label><Input value={s.currency} onChange={(e) => update("currency", e.target.value)} data-testid="settings-currency" /></div>
        </CardContent>
      </Card>

      <Card data-testid="payment-config-card">
        <CardHeader><CardTitle>Payment instructions (Lipa Namba)</CardTitle>
          <p className="text-sm text-[color:var(--muted-text)]">Since the mobile-money gateway isn&apos;t connected yet, guests get these details on their confirmation page and are asked to send the payment receipt via WhatsApp.</p>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div><Label>Payment / Lipa Namba</Label><Input value={s.payment_number || ""} onChange={(e) => update("payment_number", e.target.value)} data-testid="settings-payment-number" placeholder="e.g. 5012345 or +255 798 421 813" /></div>
          <div><Label>Registered name</Label><Input value={s.payment_name || ""} onChange={(e) => update("payment_name", e.target.value)} data-testid="settings-payment-name" placeholder="Baraka Inn Hotel" /></div>
          <div className="md:col-span-2"><Label>Extra instructions to guest</Label><Textarea rows={2} value={s.payment_instructions || ""} onChange={(e) => update("payment_instructions", e.target.value)} data-testid="settings-payment-instructions" placeholder="After sending, WhatsApp the confirmation SMS to us so reception can mark your booking as paid." /></div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>Notifications</CardTitle>
          <p className="text-sm text-[color:var(--muted-text)]">Owner receives new-booking alerts, daily/weekly/monthly summaries.</p>
        </CardHeader>
      <Card>
        <CardHeader><CardTitle>WhatsApp alert recipients</CardTitle>
          <p className="text-sm text-[color:var(--muted-text)]">
            Add every phone that should receive booking + digest alerts via <b>CallMeBot (free)</b>.
            Each phone needs its own API key: save <b>+34 644 51 95 23</b>, send it
            <b> "I allow callmebot to send me messages"</b> from that phone, paste the reply key here.
          </p>
        </CardHeader>
        <CardContent className="space-y-3">
          {(s.whatsapp_recipients || []).map((r, i) => (
            <div key={i} className="grid grid-cols-1 md:grid-cols-[1fr_1.4fr_1fr_auto] gap-2 items-end p-3 rounded-lg bg-[color:var(--sand)]" data-testid={`wa-recipient-${i}`}>
              <div><Label>Name</Label><Input value={r.name || ""} onChange={(e) => update("whatsapp_recipients", s.whatsapp_recipients.map((x, xi) => xi === i ? { ...x, name: e.target.value } : x))} data-testid={`wa-name-${i}`} placeholder="Owner / Manager" /></div>
              <div><Label>Phone (with country code)</Label><Input value={r.phone || ""} onChange={(e) => update("whatsapp_recipients", s.whatsapp_recipients.map((x, xi) => xi === i ? { ...x, phone: e.target.value } : x))} data-testid={`wa-phone-${i}`} placeholder="+255754710103" /></div>
              <div><Label>CallMeBot key</Label><Input value={r.apikey || ""} onChange={(e) => update("whatsapp_recipients", s.whatsapp_recipients.map((x, xi) => xi === i ? { ...x, apikey: e.target.value } : x))} data-testid={`wa-key-${i}`} placeholder="1234567" /></div>
              <Button variant="destructive" size="sm" onClick={() => update("whatsapp_recipients", s.whatsapp_recipients.filter((_, xi) => xi !== i))} data-testid={`wa-del-${i}`}><X className="w-4 h-4" /></Button>
            </div>
          ))}
          <div className="flex gap-2 flex-wrap">
            <Button variant="outline" onClick={() => update("whatsapp_recipients", [...(s.whatsapp_recipients || []), { name: "", phone: "", apikey: "" }])} data-testid="add-wa-recipient"><Plus className="w-4 h-4 mr-1" /> Add recipient</Button>
            <Button
              variant="outline"
              onClick={async () => {
                try {
                  const { data } = await api.post("/admin/notifications/test-whatsapp");
                  const s = (data.sent || []).length;
                  const sk = (data.skipped || []).length;
                  const f = (data.failed || []).length;
                  toast[s ? "success" : (f ? "error" : "info")](`Test sent ✓${s} · skipped (no key) ${sk} · failed ${f}`);
                } catch (e) { toast.error(formatApiError(e)); }
              }}
              data-testid="test-wa-btn"
              className="border-emerald-600 text-emerald-700 hover:bg-emerald-600 hover:text-white"
            ><Save className="w-4 h-4 mr-1" /> Send test WhatsApp</Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>Owner WhatsApp alerts (legacy single-number)</CardTitle>
          <p className="text-sm text-[color:var(--muted-text)]">
            Send booking alerts to your WhatsApp using the <b>free CallMeBot service</b>. Set-up:
            (1) save <b>+34 644 51 95 23</b> as a contact,
            (2) send it the message <b>&quot;I allow callmebot to send me messages&quot;</b> from the owner phone below,
            (3) copy the API key it replies with and paste it here.
          </p>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div><Label>Owner WhatsApp number (with country code, digits only)</Label><Input value={s.owner_whatsapp_number || ""} onChange={(e) => update("owner_whatsapp_number", e.target.value)} placeholder="255798421813" data-testid="settings-owner-wa-number" /></div>
          <div><Label>CallMeBot API key</Label><Input value={s.owner_whatsapp_apikey || ""} onChange={(e) => update("owner_whatsapp_apikey", e.target.value)} placeholder="1234567" data-testid="settings-owner-wa-key" /></div>
        </CardContent>
      </Card>


        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div><Label>Owner email</Label><Input type="email" value={s.owner_email || ""} onChange={(e) => update("owner_email", e.target.value)} data-testid="settings-owner-email" placeholder="owner@example.com" /></div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Check-in fields</CardTitle>
          <p className="text-sm text-[color:var(--muted-text)]">Toggle which fields receptionists must capture at check-in. ID photo upload is always required.</p>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {(s.check_in_fields || []).map((f, i) => (
              <div key={f.key} className="grid grid-cols-1 md:grid-cols-[1fr_auto_auto] items-center gap-3 p-3 rounded-lg bg-[color:var(--sand)]" data-testid={`checkin-field-${f.key}`}>
                <div>
                  <p className="font-medium text-sm text-[color:var(--deep)] flex items-center gap-2">
                    {f.label} {f.locked && <Lock className="w-3 h-3 text-[color:var(--muted-text)]" />}
                  </p>
                  <p className="text-xs text-[color:var(--muted-text)]">key: {f.key}</p>
                </div>
                <label className="flex items-center gap-2 text-sm">
                  <Switch checked={!!f.enabled} onCheckedChange={(v) => !f.locked && updateField(i, { enabled: v })} disabled={f.locked} data-testid={`enable-${f.key}`} />
                  <span>Enabled</span>
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <Switch checked={!!f.required} onCheckedChange={(v) => !f.locked && updateField(i, { required: v })} disabled={f.locked || !f.enabled} data-testid={`required-${f.key}`} />
                  <span>Required</span>
                </label>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>Social media</CardTitle>
          <p className="text-sm text-[color:var(--muted-text)]">Turn each channel on and paste your page URL. Enabled icons appear on the public landing page — a guest tap opens your page in a new tab.</p>
        </CardHeader>
        <CardContent className="space-y-3">
          {[
            { key: "instagram", label: "Instagram", placeholder: "https://instagram.com/barakainnhotel", color: "text-pink-600" },
            { key: "facebook", label: "Facebook", placeholder: "https://facebook.com/barakainnhotel", color: "text-blue-600" },
            { key: "twitter", label: "Twitter / X", placeholder: "https://twitter.com/barakainn", color: "text-slate-800" },
            { key: "tiktok", label: "TikTok", placeholder: "https://tiktok.com/@barakainn", color: "text-black" },
          ].map((sm) => {
            const cur = s.social_media?.[sm.key] || { enabled: false, url: "" };
            const setSM = (patch) => update("social_media", { ...(s.social_media || {}), [sm.key]: { ...cur, ...patch } });
            return (
              <div key={sm.key} className="grid grid-cols-1 md:grid-cols-[auto_1fr_auto] gap-3 items-center p-3 rounded-lg bg-[color:var(--sand)]" data-testid={`social-row-${sm.key}`}>
                <div className={`w-28 font-medium text-sm ${sm.color}`}>{sm.label}</div>
                <Input value={cur.url} onChange={(e) => setSM({ url: e.target.value })} placeholder={sm.placeholder} disabled={!cur.enabled} data-testid={`social-url-${sm.key}`} />
                <label className="flex items-center gap-2 text-sm text-[color:var(--deep)]">
                  <Switch checked={!!cur.enabled} onCheckedChange={(v) => setSM({ enabled: v })} data-testid={`social-toggle-${sm.key}`} />
                  <span>{cur.enabled ? "On" : "Off"}</span>
                </label>
              </div>
            );
          })}
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>Hero image</CardTitle>
          <p className="text-sm text-[color:var(--muted-text)]">The large image at the top of the public site.</p>
        </CardHeader>
        <CardContent>
          <div className="flex items-start gap-4 flex-wrap">
            <div className="w-72 h-40 rounded-lg overflow-hidden bg-[color:var(--sand)]">
              {s.hero_image && <img src={s.hero_image} alt="" className="w-full h-full object-cover" />}
            </div>
            <div className="flex flex-col gap-2">
              <label className="cursor-pointer inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-[color:var(--brand)]/30 hover:bg-[color:var(--sand)] text-sm">
                <Upload className="w-4 h-4" /> {uploading ? "Uploading…" : "Replace hero"}
                <input type="file" accept="image/*" className="hidden" onChange={(e) => upload(e.target.files?.[0], "hotel", (url) => update("hero_image", url))} data-testid="upload-hero" />
              </label>
              <Input value={s.hero_image || ""} onChange={(e) => update("hero_image", e.target.value)} placeholder="Or paste an image URL" data-testid="hero-url" className="w-72" />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>Inside Baraka Inn — gallery</CardTitle>
          <p className="text-sm text-[color:var(--muted-text)]">Images shown in the &quot;Inside Baraka Inn&quot; grid on the landing page.</p>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-3">
            {(s.gallery || []).map((src, i) => (
              <div key={i} className="relative w-32 h-32 rounded-lg overflow-hidden" data-testid={`gallery-item-${i}`}>
                <img src={src} alt="" className="w-full h-full object-cover" />
                <button onClick={() => update("gallery", s.gallery.filter((_, x) => x !== i))} className="absolute top-1 right-1 bg-white/90 rounded-full p-0.5" data-testid={`del-gallery-${i}`}><X className="w-3 h-3" /></button>
              </div>
            ))}
            <label className="w-32 h-32 rounded-lg border-2 border-dashed border-[color:var(--brand)]/30 flex items-center justify-center cursor-pointer hover:bg-[color:var(--sand)]">
              <Upload className="w-5 h-5 text-[color:var(--brand)]" />
              <input type="file" accept="image/*" className="hidden" onChange={(e) => upload(e.target.files?.[0], "hotel", (url) => update("gallery", [...(s.gallery || []), url]))} data-testid="upload-gallery" />
            </label>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>Services / Experiences</CardTitle>
          <p className="text-sm text-[color:var(--muted-text)]">Each service appears in the &quot;Experiences&quot; section of the landing page. Upload an image for a richer look; otherwise a default icon shows.</p>
        </CardHeader>
        <CardContent className="space-y-3">
          {(s.services || []).map((sv, i) => (
            <div key={i} className="grid grid-cols-1 md:grid-cols-[100px_1fr_2fr_auto] gap-3 items-start p-3 rounded-lg bg-[color:var(--sand)]" data-testid={`service-row-${i}`}>
              <div className="relative w-20 h-20 rounded-lg overflow-hidden bg-white border border-[color:var(--brand)]/20">
                {sv.image ? <img src={sv.image} alt={sv.title} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-[color:var(--muted-text)] text-xs">no image</div>}
                <label className="absolute bottom-1 right-1 bg-white/95 rounded-full p-1 shadow cursor-pointer" title="Upload image">
                  <Upload className="w-3 h-3 text-[color:var(--brand)]" />
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => upload(e.target.files?.[0], "services", (url) => update("services", s.services.map((x, xi) => xi === i ? { ...x, image: url } : x)))} data-testid={`upload-service-${i}`} />
                </label>
              </div>
              <Input value={sv.title} onChange={(e) => update("services", s.services.map((x, xi) => xi === i ? { ...x, title: e.target.value } : x))} placeholder="Service title" data-testid={`service-title-${i}`} />
              <Textarea rows={2} value={sv.description} onChange={(e) => update("services", s.services.map((x, xi) => xi === i ? { ...x, description: e.target.value } : x))} placeholder="Description" data-testid={`service-desc-${i}`} />
              <Button variant="destructive" size="sm" onClick={() => update("services", s.services.filter((_, xi) => xi !== i))} data-testid={`del-service-${i}`}><X className="w-4 h-4" /></Button>
            </div>
          ))}
          <Button variant="outline" onClick={() => update("services", [...(s.services || []), { title: "", description: "", image: "" }])} data-testid="add-service-btn"><Plus className="w-4 h-4 mr-1" /> Add service</Button>
        </CardContent>
      </Card>
    </div>
  );
}
